import {
  Component,
  ContentChildren,
  QueryList,
  AfterContentInit,
  ElementRef,
  ViewChild,
  Renderer2,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabWidgetComponent } from './tab-widget.component';
import { animate, style, transition, trigger } from '@angular/animations';
import {active} from "d3-transition";
import {ChipsModule} from "../../global/chips/chips/chips.module";
import {DesignSystemModule} from "../design-system.module";

@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [CommonModule, ChipsModule, DesignSystemModule],
  template: `
      <div class="tabs-header">
          <div class="tab-names-container" #tabsContainer>
              <div *ngFor="let tab of tabs; let i = index"
                   (click)="!tab.disabled && selectTab(tab, i)"
                   class="tab-name"
                   [class.active]="tab.active"
                   [class.disabled]="tab.disabled">
                  <img *ngIf="tab.iconSrc"
                       [src]="tab.iconSrc"
                       [alt]="tab.iconAlt || tab.title"
                       class="tab-icon"
                       [class.filter-primary]="!tab.active"
                       [class.filter-accent]="tab.active"
                       [class.filter-disabled]="tab.disabled">
                  <span class="tab-title">{{ tab.title }}</span>
                  <div *ngIf="tab.chips" class="ml-auto">{{tab.chips}}</div>
              </div>
              <div class="indicator" #indicator></div>
          </div>
      </div>
      <div class="tab-body">
          <ng-container *ngIf="activeTab">
              <ng-container *ngTemplateOutlet="activeTab.contentTemplate"></ng-container>
          </ng-container>
      </div>
  `,
  styles: [`
    @use "src/variables" as *;
    .tabs-header {
      border-bottom: 1px solid #F2F2F8;
      position: relative;
    }
    .tab-names-container {
      display: flex;
      position: relative;
    }
    .tab-name {
      padding: 12px 24px;
      cursor: pointer;
      position: relative;
      transition: color 0.3s;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .tab-name.active {
      color: $k-accent;
    }
    .tab-name.disabled {
      color: #9E9E9E;
      cursor: not-allowed;
      opacity: 0.6;
    }
    .tab-icon {
      width: 15px;
      height: 15px;
      object-fit: contain;
    }
    .tab-icon.filter-disabled {
      opacity: 0.5;
      filter: grayscale(100%);
    }
    .tab-title {
      display: inline-block;
    }
    .indicator {
      position: absolute;
      bottom: 0;
      height: 3px;
      background-color: $k-accent;
      transition: all 0.3s ease;
    }
    .tab-body {
      padding: 20px 0;
    }
  `],
  animations: [
    trigger('tabAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(10px)' }))
      ])
    ])
  ]
})
export class TabsWidgetComponent implements AfterContentInit {
  @ContentChildren(TabWidgetComponent) tabs!: QueryList<TabWidgetComponent>;
  @ViewChild('indicator', { static: false }) indicator!: ElementRef;
  @ViewChild('tabsContainer', { static: false }) tabsContainer!: ElementRef;

  activeTab: TabWidgetComponent | null = null;

  constructor(
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterContentInit() {
    // Default to first tab if none is active
    setTimeout(() => {
      if (this.tabs.length > 0) {
        const activeTab = this.tabs.find(tab => tab.active);
        if (activeTab) {
          this.selectTab(activeTab);
        } else {
          // Find first non-disabled tab
          const firstEnabledTab = this.tabs.find(tab => !tab.disabled);
          if (firstEnabledTab) {
            this.selectTab(firstEnabledTab);
          } else {
            // If all tabs are disabled, don't select any
            this.activeTab = null;
          }
        }
      }
    });

    // Update when tabs change
    this.tabs.changes.subscribe(() => {
      if (this.tabs.length > 0 && !this.activeTab) {
        const firstEnabledTab = this.tabs.find(tab => !tab.disabled);
        if (firstEnabledTab) {
          this.selectTab(firstEnabledTab);
        }
      }
    });
  }

  selectTab(tab: TabWidgetComponent, index?: number) {
    // Don't select if tab is disabled
    if (tab.disabled) return;

    // Deactivate all tabs
    this.tabs.forEach(tab => (tab.active = false));

    // Activate the selected tab
    tab.active = true;
    this.activeTab = tab;

    // Move the indicator
    setTimeout(() => {
      this.moveIndicator(index !== undefined ? index : this.getTabIndex(tab));
      this.cdr.detectChanges();
    });
  }

  private getTabIndex(tab: TabWidgetComponent): number {
    return this.tabs.toArray().indexOf(tab);
  }

  private moveIndicator(index: number) {
    if (!this.indicator || !this.tabsContainer) return;

    const tabElements = this.tabsContainer.nativeElement.querySelectorAll('.tab-name');
    if (!tabElements || tabElements.length === 0 || !tabElements[index]) return;

    const tabElement = tabElements[index];
    const tabPosition = tabElement.offsetLeft;
    const tabWidth = tabElement.offsetWidth;

    this.renderer.setStyle(this.indicator.nativeElement, 'width', `${tabWidth}px`);
    this.renderer.setStyle(this.indicator.nativeElement, 'left', `${tabPosition}px`);
  }

  protected readonly active = active;
}